import { datadogRum } from "@datadog/browser-rum";

import { APP_V2_APP_EVENTS, logError } from "../lib/analytics";

/**
 * Categorizes login RUM events so monitors can distinguish user friction
 * (e.g. wrong password) from genuine system failures (e.g. Cognito 5xx,
 * token refresh failure). Mirrors the worker-app pattern.
 *
 * Every login attempt — success or failure, across all flows (Cognito
 * password, Cognito magic link, Rippling SSO, token refresh, session
 * hydration) — should emit a RUM event tagged with one of the
 * `LOGIN_OUTCOME` values plus a discriminating `reason`.
 */

export const LOGIN_OUTCOME = {
  SUCCESS: "success",
  EXPECTED_ERROR: "expected_error",
  UNEXPECTED_ERROR: "unexpected_error",
} as const;

export type LoginOutcome = (typeof LOGIN_OUTCOME)[keyof typeof LOGIN_OUTCOME];

/**
 * Identity provider for the login attempt. Set as RUM global context at
 * flow entry so every subsequent RUM event in the session carries it.
 *
 * Monitors slice on this to compute per-IdP error rates.
 */
export const IDENTITY_PROVIDER = {
  COGNITO_PASSWORD: "cognito-password",
  COGNITO_MAGIC_LINK: "cognito-magic-link",
  COGNITO_PASSKEY: "cognito-passkey",
  RIPPLING_SSO: "rippling-sso",
} as const;

export type IdentityProvider = (typeof IDENTITY_PROVIDER)[keyof typeof IDENTITY_PROVIDER];

/**
 * Reason tags for error events. Cognito-mapped errors use the `auth/*`
 * code as-is for parity with existing monitor filters
 * (`@context.errorCode:"auth/..."`). Non-Cognito reasons use snake_case.
 */
export const LOGIN_REASON = {
  // Non-Cognito infrastructure reasons.
  NETWORK_FAILURE: "network_failure",
  REQUEST_ENTITY_TOO_LARGE: "request_entity_too_large",
  TOKEN_REFRESH_FAILED: "token_refresh_failed",
  SESSION_HYDRATION_FAILED: "session_hydration_failed",
  COGNITO_SESSION_INIT_FAILED: "cognito_session_init_failed",
  ACCOUNT_LINKED_RETRY_EXHAUSTED: "account_linked_retry_exhausted",
  IDP_INITIATED_RETRY_EXHAUSTED: "idp_initiated_retry_exhausted",
  SSO_REDIRECT_FAILED: "sso_redirect_failed",
  SSO_CALLBACK_FAILED: "sso_callback_failed",
  SSO_TOKEN_EXCHANGE_FORBIDDEN: "sso_token_exchange_forbidden",
  // Catch-all when no mapped code is available.
  UNKNOWN: "unknown",
} as const;

export type LoginReason = string;

/**
 * Cognito error codes that represent user friction (wrong credentials,
 * expired link, rate-limited, etc.) — alerting on these would be noisy.
 *
 * Errors NOT in this set (e.g. auth/lambda-error, auth/unknown) are treated
 * as `unexpected_error` — actual system problems worth paging on.
 * `auth/network-request-failed` is the one exception: it is handled ahead of
 * this set in `classifyCognitoError` and classified as
 * `expected_error` / `network_failure` (transient connectivity, not a defect).
 *
 * NOTE: Keep in sync with the exclusion lists in `terraform/monitor.tf`
 * (`auth/user-not-found`, `auth/invalid-link`,
 * `auth/incorrect-credentials`, `auth/token-exchange-forbidden`).
 */
const EXPECTED_COGNITO_ERROR_CODES = new Set<string>([
  "auth/user-type-not-allowed",
  "auth/user-not-found",
  "auth/incorrect-password",
  "auth/incorrect-credentials",
  "auth/invalid-link",
  "auth/re-auth-required",
  "auth/too-many-requests",
  "auth/limit-exceeded",
  "auth/invalid-code",
  "auth/expired-code",
  "auth/password-reset-required",
  "auth/invalid-password",
  "auth/token-exchange-forbidden",
]);

/**
 * Classifies a mapped Cognito error code into an `outcome` + `reason`
 * pair. Network errors are treated as `expected_error` to match the
 * existing terraform monitor exclusions (Network*Error* is filtered out
 * of system-error monitors).
 */
export function classifyCognitoError(errorCode: string): {
  outcome: LoginOutcome;
  reason: LoginReason;
} {
  if (errorCode === "auth/network-request-failed") {
    return {
      outcome: LOGIN_OUTCOME.EXPECTED_ERROR,
      reason: LOGIN_REASON.NETWORK_FAILURE,
    };
  }

  if (EXPECTED_COGNITO_ERROR_CODES.has(errorCode)) {
    return { outcome: LOGIN_OUTCOME.EXPECTED_ERROR, reason: errorCode };
  }

  // auth/lambda-error, auth/unknown, or any future code we haven't
  // explicitly classified — treat as unexpected so we get visibility.
  // Fall back to UNKNOWN for blank codes so the discriminator is never empty.
  return {
    outcome: LOGIN_OUTCOME.UNEXPECTED_ERROR,
    reason: errorCode.trim() === "" ? LOGIN_REASON.UNKNOWN : errorCode,
  };
}

/**
 * Detects whether an unknown error looks like a network failure by name
 * or message, for paths that don't go through `mapCognitoError`.
 */
export function isNetworkFailure(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false;
  }
  const candidate = error as { name?: unknown; message?: unknown };
  const name = typeof candidate.name === "string" ? candidate.name : "";
  const message = typeof candidate.message === "string" ? candidate.message : "";
  return /network/i.test(name) || /network/i.test(message);
}

interface AuthRedirectFailureLog {
  error: unknown;
  metadata: { outcome: LoginOutcome; reason: LoginReason } & Record<string, unknown>;
}

/**
 * Logs a `COGNITO_AUTH_REDIRECT_FAILURE`, routing expected transient network
 * failures away from Datadog Error Tracking.
 *
 * Amplify/core throws `NetworkError: A network error has occurred.` whenever
 * the device drops connectivity mid-request across the auth redirect flow —
 * requesting the magic-link email (`ensureCognitoUser`/`requestMagicLink`),
 * redeeming it (`completeMagicLinkSignIn`), or hydrating the session
 * (`initCognitoSession`). The user simply retries and succeeds, so these are
 * not actionable auth defects. Routing them through `logError`
 * (`datadogRum.addError`) floods Error Tracking, which groups by the raw error
 * and ignores the `outcome`/`reason` discriminators monitors rely on (issue
 * 478deec4-..., STAFF-1354).
 *
 * For an expected network failure we record a Datadog RUM action directly via
 * `datadogRum.addAction` (NOT `logEvent`, which also forwards to Segment — we
 * don't want failed-login telemetry, including `email`, flowing into Segment).
 * The action carries the same metadata, so monitors keep full visibility into
 * the network-failure rate while Error Tracking stays clean, and the Datadog
 * exposure matches the prior `logError` → `addError` path. Every other failure
 * — expected auth friction (`auth/invalid-link`, `auth/user-not-found`, etc.)
 * and genuine unexpected defects (`auth/lambda-error`, `auth/unknown`) — still
 * goes through `logError` with actionable context.
 *
 * Mirrors the non-error RUM action used for the Cognito session-lock timeout
 * (`COGNITO_SESSION_LOCK_TIMEOUT`) and the token-refresh transient-failure
 * handling in `api.ts` (`GET_COGNITO_TOKEN_TRANSIENT_FAILURE`, STAFF-1324).
 */
export function logAuthRedirectFailure({ error, metadata }: AuthRedirectFailureLog): void {
  const isExpectedNetworkFailure =
    metadata.outcome === LOGIN_OUTCOME.EXPECTED_ERROR &&
    metadata.reason === LOGIN_REASON.NETWORK_FAILURE;

  if (isExpectedNetworkFailure) {
    datadogRum.addAction(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_NETWORK_FAILURE, metadata);
    return;
  }

  logError(APP_V2_APP_EVENTS.COGNITO_AUTH_REDIRECT_FAILURE, { error, metadata });
}

/**
 * Sets the identity provider on the RUM global context so every
 * subsequent RUM event in this session carries `@context.identityProvider`.
 *
 * Call this at the entry point of each login flow (password submit, magic
 * link request, SSO redirect, etc.). Pass `undefined` to clear, e.g. on
 * logout.
 */
/**
 * Metadata for the `GET_COGNITO_TOKEN_FAILURE` log emitted by the axios
 * request interceptor when token refresh fails. Lives here so api.ts stays
 * under its max-lines budget.
 */
export function tokenFailureMetadata(error: unknown): {
  source: string;
  outcome: LoginOutcome;
  reason: LoginReason;
} {
  return {
    source: "axios-interceptor",
    outcome: LOGIN_OUTCOME.UNEXPECTED_ERROR,
    reason: isNetworkFailure(error)
      ? LOGIN_REASON.NETWORK_FAILURE
      : LOGIN_REASON.TOKEN_REFRESH_FAILED,
  };
}

export function setLoginIdentityProvider(provider: IdentityProvider | undefined): void {
  if (provider === undefined) {
    datadogRum.removeGlobalContextProperty("identityProvider");
    return;
  }
  datadogRum.setGlobalContextProperty("identityProvider", provider);
}
